/**
 * A list of saved, categorized preferences where every add, edit and removal saves immediately on its
 * own (through the per-entry preference flows) rather than with the page's Save button. That's what
 * keeps a stale form from overwriting a fact Elara remembered while it was open.
 *
 * The sibling `PreferenceList` edits a plain array that the page saves as a whole; it remains for
 * lists that aren't categorized preferences (e.g. travel credit cards).
 *
 * "Avoid" entries ("Never recommend …") carry a hidden hotel/place reference, so they can be removed
 * but not reworded. Entries without an id predate categorization and are shown read-only until the
 * next save categorizes them.
 */
import type { ProfilePreference } from '@elara/domain/models/profile-preferences';
import type { CategorizedPreference } from '@elara/domain/models/schemas/shared-schemas';

import { useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';
import { isCategorizedPreference } from '@elara/domain/models/profile-preferences';

import { Badge, Button, Label, Textarea } from './index.js';
import { MaterialIcon } from './MaterialIcon.js';

interface SavedPreferenceListProps {
  label: string;
  entries: ProfilePreference[];
  onAdd: (value: string) => Promise<void>;
  onUpdate: (entry: CategorizedPreference, value: string) => Promise<void>;
  onRemove: (entry: CategorizedPreference) => Promise<void>;
  placeholder?: string;
  showLabel?: boolean;
}

export function SavedPreferenceList({
  label,
  entries,
  onAdd,
  onUpdate,
  onRemove,
  placeholder,
  showLabel = true,
}: SavedPreferenceListProps) {
  const [newValue, setNewValue] = useState('');
  const [editingId, setEditingId] = useState<string | undefined>();
  const [editingValue, setEditingValue] = useState('');
  /** The entry id (or `'new'`) whose save is in flight. */
  const [busyId, setBusyId] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  const run = async (id: string, action: () => Promise<void>) => {
    setBusyId(id);
    setError(undefined);
    try {
      await action();
      return true;
    } catch (err) {
      console.error('[SavedPreferenceList] Failed to save preference', err);
      setError('We couldn’t save that change. Please try again.');
      return false;
    } finally {
      setBusyId(undefined);
    }
  };

  const addItem = async () => {
    const trimmed = newValue.trim();
    if (!trimmed || busyId) return;
    if (await run('new', async () => onAdd(trimmed))) setNewValue('');
  };

  const startEditing = (entry: CategorizedPreference) => {
    setEditingId(entry.id);
    setEditingValue(entry.value);
  };

  const cancelEdit = () => {
    setEditingId(undefined);
    setEditingValue('');
  };

  const saveEdit = async (entry: CategorizedPreference) => {
    const trimmed = editingValue.trim();
    if (trimmed === entry.value) return cancelEdit();
    const ok = await run(entry.id, async () => (trimmed ? onUpdate(entry, trimmed) : onRemove(entry)));
    if (ok) cancelEdit();
  };

  return (
    <div className="space-y-3">
      <Label className={cn(!showLabel && 'sr-only')}>{label}</Label>

      {entries.length > 0 && (
        <ul className="space-y-2">
          {entries.map((entry, index) => {
            const saved = isCategorizedPreference(entry) ? entry : undefined;
            const isAvoid = saved?.avoid === true;
            const isEditing = saved !== undefined && editingId === saved.id;
            const canEdit = saved !== undefined && !isAvoid;
            return (
              <li
                key={saved?.id ?? `legacy-${index}`}
                className={cn(
                  'flex items-start pl-3 pr-2 py-1 group rounded-sm border bg-background-secondary transition-colors',
                  isEditing ? 'bg-background border border-accent flex-col gap-1 pb-2' : 'gap-2',
                  canEdit && !isEditing && 'hover:border-input cursor-pointer',
                )}
                onClick={() => canEdit && !isEditing && startEditing(saved)}
              >
                {isEditing ? (
                  <>
                    <Textarea
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          void saveEdit(saved);
                        }
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      rows={1}
                      autoFocus
                      autoResize
                      className="flex-1 px-0 py-2.5 rounded-none border-0 w-full"
                    />
                    <div className="flex items-center gap-2 w-full">
                      {saved.source === 'elara' && (
                        <span className="text-xs text-muted-foreground">Added by Elara</span>
                      )}
                      <div className="flex-1" />
                      <Button variant="ghost" size="sm" onMouseDown={(e) => (e.preventDefault(), cancelEdit())}>
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        isBusy={busyId === saved.id}
                        onMouseDown={(e) => (e.preventDefault(), void saveEdit(saved))}
                      >
                        Save
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm py-2.5 whitespace-pre-wrap">{entry.value}</span>
                    {/* my-1 centres the 32px button on the first 40px line of text. */}
                    <div className="flex items-center gap-2 my-1">
                      {isAvoid && <Badge variant="secondary">Avoid</Badge>}
                      {saved?.source === 'elara' && !isAvoid && (
                        <span className="text-xs text-muted-foreground whitespace-nowrap">Added by Elara</span>
                      )}
                      {saved && (
                        <Button
                          variant="icon"
                          size="icon-sm"
                          aria-label={`Remove ${entry.value}`}
                          isBusy={busyId === saved.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            void run(saved.id, async () => onRemove(saved));
                          }}
                          className="h-8 w-8 hover:text-destructive"
                        >
                          <MaterialIcon icon="delete" />
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* One row: the input grows as it wraps and the round add button stays pinned bottom-right. */}
      <div className="group/add flex items-end gap-2 pl-3 pr-2 border border-input focus-within:border-accent rounded-sm">
        <Textarea
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void addItem();
            }
          }}
          rows={1}
          autoResize
          disabled={busyId === 'new'}
          placeholder={placeholder || `Add ${label.toLowerCase()}`}
          aria-label={`Add ${label.toLowerCase()}`}
          className="flex-1 min-w-0 px-0 py-3.5 rounded-none border-0"
        />
        <Button
          size="icon-sm"
          // A plain icon while empty, filled once there's something to add.
          variant={newValue.trim() ? 'default' : 'icon'}
          className="mb-2 h-8 w-8 flex-shrink-0 rounded-full"
          aria-label="Add"
          isBusy={busyId === 'new'}
          onMouseDown={(e) => {
            e.preventDefault(); // Keep focus in the textarea
            void addItem();
          }}
          disabled={!newValue.trim()}
        >
          <MaterialIcon icon="add" />
        </Button>
      </div>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
